import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Grid,
  Paper,
  Box,
  Button,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import SectionForm from "./questonsInfo/SectionForm";
import ExamPaperInfo from "./questonsInfo/ExamPaperInfo";
import PaperForm from "./questonsInfo/PaperForm";
import {
  GET_ALL_EXAM_PAPERS,
  GET_ALL_EXAM_PAPERS_QUESTIONS,
} from "utils/graphql/services/ai-evaluation/queries";
import { UPDATE_EXAM_PAPER_QUESTIONS } from "utils/graphql/services/ai-evaluation/mutations";
import autoGradeClient from "utils/graphql/autoGradeClient";

function UpdateQuestionsPage() {
  const history = useHistory();
  const { examPaperId } = useParams<{ examPaperId: string }>();
  const [examPaper, setExamPaper] = useState<any>(null);
  const [paperNode, setPaperNode] = useState<any>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [updateSummary, setUpdateSummary] = useState<string>("");
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const originalExamPaper = useRef<any>(null);
  const originalPaperNode = useRef<any>(null);
  const originalSections = useRef<any[]>([]);

  const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== typeof obj2) return false;

    if (typeof obj1 !== "object") return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }, []);

  const getPaperTrackableFields = useCallback((paper: any) => {
    if (!paper) return null;
    return {
      id: paper.id,
      marks: paper.marks,
      choiceCount: paper.choiceCount,
    };
  }, []);

  const getSectionTrackableFields = useCallback((section: any) => {
    if (!section) return null;
    return {
      id: section.id,
      title: section.title,
      order: section.order,
      marks: section.marks,
      choiceCount: section.choiceCount,
    };
  }, []);

  const getQuestionTrackableFields = useCallback((question: any) => {
    if (!question) return null;
    return {
      id: question.id,
      questionNumber: question.questionNumber,
      questionType: question.questionType,
      marks: question.marks,
      choiceCount: question.choiceCount,
      subjectName: question.subjectName,
      chapterName: question.chapterName,
      topicName: question.topicName,
    };
  }, []);

  const { data, loading } = useQuery(GET_ALL_EXAM_PAPERS, {
    client: autoGradeClient,
    variables: { input: { id: parseInt(examPaperId) } },
  });

  const examPapers = useMemo(
    () => data?.getExamPapers?.examPapers || [],
    [data]
  );

  useEffect(() => {
    if (examPapers.length > 0) {
      setExamPaper(examPapers[0]);
    }
  }, [examPapers]);

  const { data: questionsData, refetch: refetchQuestions } = useQuery(
    GET_ALL_EXAM_PAPERS_QUESTIONS,
    {
      client: autoGradeClient,
      variables: { input: { examPaperId: parseInt(examPaperId) } },
    }
  );

  const { refetch: refetchExamPaper } = useQuery(GET_ALL_EXAM_PAPERS, {
    client: autoGradeClient,
    variables: { input: { id: parseInt(examPaperId) } },
  });

  const [updateExamPaperQuestions] = useMutation(UPDATE_EXAM_PAPER_QUESTIONS, {
    client: autoGradeClient,
  });

  useEffect(() => {
    if (!questionsData?.getExamPaperQuestions?.examPaperQuestions) return;

    const fetchedQuestions =
      questionsData.getExamPaperQuestions.examPaperQuestions;

    let paperNodeData =
      fetchedQuestions.find((q) => q.nodeType === "Paper") || null;

    const sections: any[] = [];

    console.log("fetchedQuestions", fetchedQuestions);
    const sectionNodes = fetchedQuestions.filter(
      (q) => q.nodeType === "Section"
    );

    sectionNodes
      .sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0))
      .forEach((sectionItem) => {
        const sectionQuestions = fetchedQuestions
          .filter(
            (q) =>
              q.nodeType === "MainQuestion" &&
              q.sectionOrder === sectionItem.sectionOrder
          )
          .map((q) => ({
            id: q.id,
            questionNumber: q.questionNumber,
            questionType: q.questionType,
            marks: q.marks,
            choiceCount: q.choiceCount,
            nodeType: q.nodeType,
            subjectName: q.subjectName,
            chapterName: q.chapterName,
            topicName: q.topicName,
            subjectId: q.subjectId || "",
            chapterId: q.chapterId || "",
            topicId: q.topicId || "",
          }));

        sections.push({
          id: sectionItem.id,
          title: sectionItem.sectionTitle || "",
          order: sectionItem.sectionOrder || 0,
          marks: sectionItem.marks || "",
          choiceCount: sectionItem.choiceCount || 0,
          questions: sectionQuestions,
        });
      });

    setPaperNode(paperNodeData);
    setSections(sections);
    originalPaperNode.current = JSON.parse(JSON.stringify(paperNodeData));
    originalSections.current = JSON.parse(JSON.stringify(sections));
  }, [questionsData]);

  const handleAddSection = () => {
    if (!examPaper && !paperNode) return;

    const totalSections = examPaper?.totalSections || paperNode?.totalSections;
    if (sections.length >= totalSections) {
      setErrorMsg(`You can only add ${totalSections} sections`);
      return;
    }

    setSections((prev) => [
      ...prev,
      {
        id: null,
        title: "",
        order: prev.length + 1,
        choiceCount: 0,
        marks: "",
        questions: [],
      },
    ]);
  };

  const updateSection = (index: number, newData: any) => {
    setSections((prev) => {
      const copy = [...prev];
      copy[index] = newData;
      return copy;
    });
  };

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const isPaperChanged = useCallback(() => {
    if (!originalPaperNode.current || !paperNode) return false;

    const currentTrackable = getPaperTrackableFields(paperNode);
    const originalTrackable = getPaperTrackableFields(
      originalPaperNode.current
    );

    return !deepEqual(currentTrackable, originalTrackable);
  }, [paperNode, getPaperTrackableFields, deepEqual]);

  const isSectionChanged = useCallback(
    (currentSection: any, originalSection: any) => {
      if (!originalSection) return true;

      const currentTrackable = getSectionTrackableFields(currentSection);
      const originalTrackable = getSectionTrackableFields(originalSection);

      return !deepEqual(currentTrackable, originalTrackable);
    },
    [getSectionTrackableFields, deepEqual]
  );

  const isQuestionChanged = useCallback(
    (currentQuestion: any, originalQuestion: any) => {
      if (!originalQuestion) return true;

      const currentTrackable = getQuestionTrackableFields(currentQuestion);
      const originalTrackable = getQuestionTrackableFields(originalQuestion);

      return !deepEqual(currentTrackable, originalTrackable);
    },
    [getQuestionTrackableFields, deepEqual]
  );

  const checkForChanges = useCallback(() => {
    if (isPaperChanged()) return true;
    for (let i = 0; i < sections.length; i++) {
      const currentSection = sections[i];
      const originalSection = originalSections.current[i];

      if (isSectionChanged(currentSection, originalSection)) return true;

      const currentQuestions = currentSection.questions || [];
      const originalQuestions = originalSection?.questions || [];

      for (let j = 0; j < currentQuestions.length; j++) {
        const currentQuestion = currentQuestions[j];
        const originalQuestion = originalQuestions[j];

        if (isQuestionChanged(currentQuestion, originalQuestion)) return true;
      }
    }

    return false;
  }, [sections, isPaperChanged, isSectionChanged, isQuestionChanged]);

  useEffect(() => {
    const newHasChanges = checkForChanges();
    if (newHasChanges !== hasChanges) {
      setHasChanges(newHasChanges);
    }
  }, [paperNode, sections, checkForChanges, hasChanges]);

  const logEditedNode = useCallback((nodeType: string, nodeData: any) => {
    console.log(`🔥 ${nodeType} Node Edited:`, {
      nodeType,
      id: nodeData.id,
      editedFields: nodeData,
      timestamp: new Date().toISOString(),
    });
  }, []);

  const generateEditedNodes = useCallback(() => {
    const result: any[] = [];

    if (isPaperChanged() && paperNode) {
      const paperNodeData = {
        id: paperNode.id,
        chapterId: paperNode.chapterId || "",
        chapterName: paperNode.chapterName || "",
        choiceCount: paperNode.choiceCount || 0,
        examPaperId: paperNode.examPaperId,
        marks: paperNode.marks || 0,
        nodeType: "Paper",
        questionNumber: paperNode.questionNumber || "",
        questionSortOrder: paperNode.questionSortOrder || "",
        questionType: paperNode.questionType || "",
        sectionOrder: paperNode.sectionOrder || "",
        sectionTitle: paperNode.sectionTitle || "",
        subjectId: paperNode.subjectId || "",
        subjectName: paperNode.subjectName || "",
        topicId: paperNode.topicId || "",
        topicName: paperNode.topicName || "",
      };
      result.push(paperNodeData);
      logEditedNode("Paper", paperNodeData);
    }

    sections.forEach((currentSection, sectionIndex) => {
      const originalSection = originalSections.current[sectionIndex];

      if (isSectionChanged(currentSection, originalSection)) {
        const sectionNode = {
          id: currentSection.id,
          chapterId: currentSection.chapterId || "",
          chapterName: currentSection.chapterName || "",
          choiceCount: currentSection.choiceCount || 0,
          examPaperId: examPaper?.id || paperNode?.examPaperId,
          marks: currentSection.marks || "",
          nodeType: "Section",
          questionNumber: currentSection.questionNumber || "",
          questionSortOrder: currentSection.questionSortOrder || "",
          questionType: currentSection.questionType || "",
          sectionOrder: currentSection.order || 0,
          sectionTitle: currentSection.title || "",
          subjectId: currentSection.subjectId || "",
          subjectName: currentSection.subjectName || "",
          topicId: currentSection.topicId || "",
          topicName: currentSection.topicName || "",
        };
        result.push(sectionNode);
        logEditedNode("Section", sectionNode);
      }

      currentSection.questions.forEach(
        (currentQuestion: any, questionIndex: number) => {
          const originalQuestion = originalSection?.questions?.[questionIndex];

          if (isQuestionChanged(currentQuestion, originalQuestion)) {
            const questionNode = {
              id: currentQuestion.id,
              chapterId: currentQuestion.chapterId || "",
              chapterName: currentQuestion.chapterName || "",
              choiceCount: currentQuestion.choiceCount || 0,
              examPaperId: examPaper?.id || paperNode?.examPaperId,
              marks: currentQuestion.marks || 0,
              nodeType: "MainQuestion",
              questionNumber: currentQuestion.questionNumber || "",
              questionSortOrder: questionIndex + 1,
              questionType: currentQuestion.questionType || "",
              sectionOrder: currentSection.order || 0,
              sectionTitle: currentSection.title || "",
              subjectId: currentQuestion.subjectId || "",
              subjectName: currentQuestion.subjectName || "",
              topicId: currentQuestion.topicId || "",
              topicName: currentQuestion.topicName || "",
            };
            result.push(questionNode);
            logEditedNode("MainQuestion", questionNode);
          }
        }
      );
    });

    return result;
  }, [
    paperNode,
    sections,
    isPaperChanged,
    isSectionChanged,
    isQuestionChanged,
    logEditedNode,
    examPaper,
  ]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      setErrorMsg("No changes detected to save");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const editedNodes = generateEditedNodes();
      const groupedByType = {
        Paper: editedNodes.filter((n) => n.nodeType === "Paper"),
        Section: editedNodes.filter((n) => n.nodeType === "Section"),
        MainQuestion: editedNodes.filter((n) => n.nodeType === "MainQuestion"),
      };
      const response = await updateExamPaperQuestions({
        variables: {
          input: editedNodes,
        },
      });

      const nodeTypeCounts = {
        Paper: groupedByType.Paper.length,
        Section: groupedByType.Section.length,
        Question: groupedByType.MainQuestion.length,
      };

      const updateDetails = Object.entries(nodeTypeCounts)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => `${count} ${type}${count > 1 ? "s" : ""}`)
        .join(", ");

      setSuccessMsg(`Successfully updated ${updateDetails}!`);
      setUpdateSummary(updateDetails);
      setShowUpdateDialog(true);
      setIsRefreshing(true);

      try {
        const [questionsResult, examPaperResult] = await Promise.all([
          refetchQuestions(),
          refetchExamPaper(),
        ]);

        if (questionsResult?.data?.getExamPaperQuestions?.examPaperQuestions) {
          const refreshedQuestions =
            questionsResult.data.getExamPaperQuestions.examPaperQuestions;

          let refreshedPaperNode =
            refreshedQuestions.find((q) => q.nodeType === "Paper") || null;
          const refreshedSections: any[] = [];
          const refreshedSectionNodes = refreshedQuestions.filter(
            (q) => q.nodeType === "Section"
          );

          refreshedSectionNodes
            .sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0))
            .forEach((sectionItem) => {
              const sectionQuestions = refreshedQuestions
                .filter(
                  (q) =>
                    q.nodeType === "MainQuestion" &&
                    q.sectionOrder === sectionItem.sectionOrder
                )
                .map((q) => ({
                  id: q.id,
                  questionNumber: q.questionNumber,
                  questionType: q.questionType,
                  marks: q.marks,
                  choiceCount: q.choiceCount,
                  nodeType: q.nodeType,
                  subjectName: q.subjectName,
                  chapterName: q.chapterName,
                  topicName: q.topicName,
                  subjectId: q.subjectId,
                  chapterId: q.chapterId,
                  topicId: q.topicId,
                }));

              refreshedSections.push({
                id: sectionItem.id,
                title: sectionItem.sectionTitle || "",
                order: sectionItem.sectionOrder || 0,
                marks: sectionItem.marks || "",
                choiceCount: sectionItem.choiceCount || 0,
                questions: sectionQuestions,
              });
            });

          setPaperNode(refreshedPaperNode);
          setSections(refreshedSections);

          originalPaperNode.current = JSON.parse(
            JSON.stringify(refreshedPaperNode)
          );
          originalSections.current = JSON.parse(
            JSON.stringify(refreshedSections)
          );
        }
      } catch (refreshError) {
      } finally {
        setIsRefreshing(false);
      }
      setTimeout(() => {
        setHasChanges(false);
      }, 500);
    } catch (error: any) {
      let errorMessage = "Failed to update questions. Please try again.";

      if (error?.graphQLErrors?.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setErrorMsg(`Update Failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasChanges,
    generateEditedNodes,
    updateExamPaperQuestions,
    paperNode,
    sections,
    refetchQuestions,
    refetchExamPaper,
  ]);

  return (
    <Box sx={{ p: 3, minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        sx={{
          backgroundColor: "white",
          padding: 2,
          borderRadius: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          Update Questions
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {loading || !examPaper || isRefreshing ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {isRefreshing ? "Refreshing data..." : "Loading exam paper..."}
              </Typography>
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <ExamPaperInfo
                  examPaperId={examPaperId}
                  onChange={(field, value) =>
                    setExamPaper((prev) => ({ ...prev, [field]: value }))
                  }
                />
              </Box>

              {/* Paper Configuration Form */}
              {paperNode && (
                <Box sx={{ mb: 3 }}>
                  <PaperForm
                    data={paperNode}
                    onChange={(newData) => setPaperNode(newData)}
                  />
                </Box>
              )}

              {sections.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  {sections.map((sec, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <SectionForm
                        index={idx}
                        data={sec}
                        onChange={(newData) => updateSection(idx, newData)}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No sections available for this exam paper.
                  </Typography>
                </Paper>
              )}

              {sections.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 4,
                    pt: 3,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => history.goBack()}
                    sx={{ minWidth: 120, height: 42 }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!hasChanges || isLoading || isRefreshing}
                    startIcon={
                      isLoading || isRefreshing ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                    sx={{
                      minWidth: 200,
                      height: 42,
                      backgroundColor: hasChanges ? undefined : "#f5f5f5",
                      color: hasChanges ? undefined : "#bdbdbd",
                      "&:hover": {
                        backgroundColor: hasChanges ? undefined : "#f5f5f5",
                      },
                    }}
                  >
                    {isLoading
                      ? "Updating..."
                      : isRefreshing
                      ? "Refreshing..."
                      : `Update Questions`}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: "medium",
          }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: "medium",
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>

      {/* Success Update Dialog */}
      {/* <Dialog
        open={showUpdateDialog}
        onClose={() => setShowUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Successful!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your exam paper has been successfully updated!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Updated:</strong> {updateSummary}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowUpdateDialog(false)}
            variant="contained"
            color="primary"
          >
            Great!
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  );
}

export default UpdateQuestionsPage;
